import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LabelConfig, LabelMargins, PrintSettings, MediaType } from '@/types'
import { DPI } from '@/lib/utils/units'

const DEFAULT_MARGINS: LabelMargins = {
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
}

const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  printSpeed: 4,      // 4 inches/second - common default
  darkness: 15,       // Medium darkness
  mediaType: 'gap',   // Most common label type
  gapLength: 3,       // 3mm gap between labels
}

interface LabelState {
  label: LabelConfig
  setLabel: (label: LabelConfig) => void
  setWidth: (width: number) => void
  setHeight: (height: number) => void
  setDPI: (dpi: DPI) => void
  setName: (name: string) => void
  setMargins: (margins: LabelMargins) => void
  setPrintSettings: (settings: PrintSettings) => void
  setPrintSpeed: (speed: number) => void
  setDarkness: (darkness: number) => void
  setMediaType: (mediaType: MediaType) => void
  setGapLength: (gapLength: number) => void
}

export const useLabelStore = create<LabelState>()(
  persist(
    (set) => ({
      label: {
        width: 100,  // 100mm default
        height: 50,  // 50mm default
        dpi: 203,
        name: 'Untitled Label',
        margins: DEFAULT_MARGINS,
        printSettings: DEFAULT_PRINT_SETTINGS,
      },
      setLabel: (label) => set({
        label: {
          ...label,
          margins: label.margins || DEFAULT_MARGINS,
          printSettings: label.printSettings || DEFAULT_PRINT_SETTINGS,
        }
      }),
      setWidth: (width) => set((state) => ({ label: { ...state.label, width } })),
      setHeight: (height) => set((state) => ({ label: { ...state.label, height } })),
      setDPI: (dpi) => set((state) => ({ label: { ...state.label, dpi } })),
      setName: (name) => set((state) => ({ label: { ...state.label, name } })),
      setMargins: (margins) => set((state) => ({ label: { ...state.label, margins } })),
      setPrintSettings: (printSettings) => set((state) => ({ label: { ...state.label, printSettings } })),
      setPrintSpeed: (printSpeed) => set((state) => ({
        label: { ...state.label, printSettings: { ...state.label.printSettings, printSpeed } }
      })),
      setDarkness: (darkness) => set((state) => ({
        label: { ...state.label, printSettings: { ...state.label.printSettings, darkness } }
      })),
      setMediaType: (mediaType) => set((state) => ({
        label: { ...state.label, printSettings: { ...state.label.printSettings, mediaType } }
      })),
      setGapLength: (gapLength) => set((state) => ({
        label: { ...state.label, printSettings: { ...state.label.printSettings, gapLength } }
      })),
    }),
    {
      name: 'zpl-label-config',
      // Migration for existing stored data
      migrate: (persistedState: unknown) => {
        const state = persistedState as { label?: LabelConfig }
        if (state?.label) {
          if (!state.label.margins) {
            state.label.margins = DEFAULT_MARGINS
          }
          if (!state.label.printSettings) {
            state.label.printSettings = DEFAULT_PRINT_SETTINGS
          }
        }
        return state as LabelState
      },
      version: 2,
    }
  )
)
